import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Save, ArrowRight, GraduationCap, User, Mail, Smartphone, Globe, Calendar, MapPin, School, FileText, Upload, CheckCircle } from 'lucide-react';
import Header from '../common/Header';

const Stage2Form = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    // Student Photo Upload
    studentPhoto: null,
    
    // Permanent Address Details
    country: 'India',
    state: 'Karnataka',
    district: 'Karnataka - Bengaluru Urban',
    city: 'BANGALORE',
    addressLine1: '',
    addressLine2: '',
    pinCode: '',
    isPermanentSameAsCorrespondence: 'Yes',
    
    // Correspondence Address Details
    correspondenceCountry: '',
    correspondenceState: '',
    correspondenceDistrict: '',
    correspondenceCity: '',
    correspondenceAddressLine1: '',
    correspondenceAddressLine2: '',
    correspondencePinCode: '',
    
    // Current School Details
    currentSchoolName: '',
    currentSchoolCountry: 'India',
    currentSchoolState: 'Karnataka',
    currentSchoolCity: 'BANGALORE',
    
    // School Duration
    studyingFromDate: '',
    studyingTillDate: '',
    reasonForLeaving: '',
    
    // School History
    everExpelled: 'No',
    expulsionReason: '',
    lastSchoolRecognized: 'No',
    transferCertificateAvailable: 'No',
    
    // Student Residency
    studentResidesWith: 'Mother & Father',
    
    // Parent 1 Details
    parent1Name: '',
    parent1Relationship: 'Father',
    parent1Email: '',
    parent1Phone: '',
    parent1Profession: '',
    parent1AnnualIncome: '',
    parent1OfficeAddress: '',
    parent1Photo: null,
    
    // Parent 2 Details
    parent2Name: '',
    parent2Relationship: 'Mother',
    parent2Email: '',
    parent2Phone: '',
    parent2Profession: '',
    parent2AnnualIncome: '',
    parent2OfficeAddress: '',
    parent2Photo: null,
    
    // Emergency Contact
    emergencyContactPerson: 'Parent 1',
    accountsFeePerson: 'Parent 1',
    
    // Local Guardian
    localContactBangalore: 'No',
    localContactIndore: 'No',
    localContactAhmedabad: 'No',
    localGuardianName: '',
    localGuardianOccupation: '',
    localGuardianOrganization: '',
    localGuardianMobile: '',
    localGuardianEmail: '',
    localGuardianAddress: '',
    localGuardianIdProof: null,
    
    // Additional Guardian Details
    additionalGuardian1Name: '',
    additionalGuardian1Occupation: '',
    additionalGuardian1Mobile: '',
    additionalGuardian1Address: '',
    
    additionalGuardian2Name: '',
    additionalGuardian2Occupation: '',
    additionalGuardian2Mobile: '',
    additionalGuardian2Address: '',
    
    // Application Questions
    considerationsForApplying: '',
    otherSchoolsApplied: '',
    howHeardAboutSchool: '',
    
    // Student Achievements
    achievementsAreas: [],
    sportsAchievements: '',
    performingArtsAchievements: '',
    responsibilityAchievements: '',
    otherAchievements: '',
    
    // Signatures
    motherSignature: null,
    fatherSignature: null,
    
    // Documents
    studentAadharCard: null,
    studentAadharNumber: '',
    studentBirthCertificate: null,
    certificationOfMerit: null,
    
    // Parent Documents
    fatherAadharCard: null,
    fatherAadharNumber: '',
    fatherPanCard: null,
    fatherPanNumber: '',
    fatherAddressProof: null,
    
    motherAadharCard: null,
    motherAadharNumber: '',
    motherPanCard: null,
    motherPanNumber: '',
    motherAddressProof: null,
    
    // Additional Documents
    studentPassport: null,
    studentVisa: null,
    studentOciCard: null,
    fatherPassport: null,
    motherPassport: null,
    fatherVisa: null,
    motherVisa: null,
    
    // Language and Career Options
    mandatoryAgreement: 'Agree',
    secondLanguageOptions: 'Hindi',
    thirdLanguageOptions: 'Kannada',
    mandatoryCareerPrepSubjects: 'Career Readiness & life skills (75 Hours Program), Academic Foundation - HOT( Higher order thinking skills) (75 Hours Program)',
    
    // Optional Subjects
    optionalSubjects: 'Apply',
    streamChosen: '',
    mandatorySubjects: '',
    mandatoryLanguage: '',
    
    // Consent
    integratedCoursesConsent: false,
    entranceExamGuidance: '',
    
    // CRM Fields
    addedTime: '',
    crmStatus: 'New Record - Record updated',
    referrerName: '',
    taskOwner: 'ajay@theacademiccity.com',
    comments: 'No Comments'
  });

  const [phoneNumber, setPhoneNumber] = useState('');
  const [dialCode, setDialCode] = useState('');
  const [countryName, setCountryName] = useState('');

  const achievementAreas = [
    'Sports',
    'Performing arts',
    'Positions of responsibility',
    'Academic excellence',
    'Community service',
    'Leadership',
    'Creative arts',
    'Technology'
  ];

  const countries = ['India', 'USA', 'UK', 'Canada', 'Australia', 'Other'];
  const states = ['Karnataka', 'Maharashtra', 'Tamil Nadu', 'Telangana', 'Andhra Pradesh', 'Other'];
  const districts = ['Karnataka - Bengaluru Urban', 'Karnataka - Bengaluru Rural', 'Other'];
  const cities = ['BANGALORE', 'MYSORE', 'MANGALORE', 'Other'];
  const relationships = ['Father', 'Mother', 'Guardian', 'Grandparent', 'Other'];
  const incomeRanges = ['0', '1-5 lakhs', '5-10 lakhs', '10-15 lakhs', '15-20 lakhs', '20+ lakhs'];
  const yesNoOptions = ['Yes', 'No'];

  useEffect(() => {
    loadExistingData();
    
    // Cleanup function to revoke object URLs when component unmounts
    return () => {
      // Revoke all object URLs to prevent memory leaks
      Object.keys(formData).forEach(key => {
        if (formData[key] && formData[key].type && formData[key].type.startsWith('image/')) {
          URL.revokeObjectURL(createThumbnail(formData[key]));
        }
      });
    };
  }, []);

  const loadExistingData = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;

      const response = await fetch('/api/application/status', {
        headers: { 'x-auth-token': token }
      });

      if (response.ok) {
        const data = await response.json();
        if (data.hasApplication && data.stage2) {
          setFormData(prev => ({ ...prev, ...data.stage2 }));
        }
      }
    } catch (error) {
      console.error('Error loading existing data:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFileChange = (e, fieldName) => {
    const file = e.target.files[0];
    setFormData(prev => ({
      ...prev,
      [fieldName]: file
    }));
  };

  // Function to create thumbnail URL for image files
  const createThumbnail = (file) => {
    if (file && file.type.startsWith('image/')) {
      return URL.createObjectURL(file);
    }
    return null;
  };

  // Function to render file preview (thumbnail for images, filename for others)
  const renderFilePreview = (file, fieldName) => {
    if (!file) return null;

    const handleRemoveFile = () => {
      setFormData(prev => ({ ...prev, [fieldName]: null }));
      // Reset the file input
      const fileInput = document.querySelector(`input[name="${fieldName}"]`);
      if (fileInput) {
        fileInput.value = '';
      }
    };

    if (file.type.startsWith('image/')) {
      return (
        <div className="flex items-center space-x-4">
          <div className="relative">
            <img
              src={createThumbnail(file)}
              alt={`${fieldName} preview`}
              className="w-20 h-20 object-cover rounded-lg border border-gray-300"
            />
            <button
              type="button"
              onClick={handleRemoveFile}
              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-600"
              title="Remove file"
            >
              ×
            </button>
          </div>
          <div className="text-sm text-gray-600">
            <p className="font-medium">{file.name}</p>
            <p className="text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
          </div>
        </div>
      );
    } else {
      return (
        <div className="flex items-center space-x-4">
          <div className="bg-gray-100 rounded-lg p-3">
            <FileText className="h-8 w-8 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">
            <p className="font-medium">{file.name}</p>
            <p className="text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
          </div>
          <button
            type="button"
            onClick={handleRemoveFile}
            className="text-red-500 hover:text-red-700"
            title="Remove file"
          >
            ×
          </button>
        </div>
      );
    }
  };

  const handleAchievementChange = (area) => {
    setFormData(prev => ({
      ...prev,
      achievementsAreas: prev.achievementsAreas.includes(area)
        ? prev.achievementsAreas.filter(a => a !== area)
        : [...prev.achievementsAreas, area]
    }));
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');

    try {
      const token = localStorage.getItem('token');
      const formDataToSend = new FormData();
      
      // Append all form data
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          if (key.includes('Photo') || key.includes('Signature') || key.includes('Card') || key.includes('Certificate')) {
            if (formData[key]) {
              formDataToSend.append(key, formData[key]);
            }
          } else if (Array.isArray(formData[key])) {
            formDataToSend.append(key, JSON.stringify(formData[key]));
          } else {
            formDataToSend.append(key, formData[key]);
          }
        }
      });

      const response = await fetch('/api/application/stage2', {
        method: 'POST',
        headers: { 'x-auth-token': token },
        body: formDataToSend
      });

      if (response.ok) {
        setSuccess('Stage 2 application saved successfully!');
        setTimeout(() => setSuccess(''), 3000);
      } else {
        const errorData = await response.json();
        setError(errorData.error || 'Failed to save application');
      }
    } catch (error) {
      setError('Failed to save application. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = async () => {
    setLoading(true);
    setError('');

    try {
      // Validate required fields
      if (!formData.studentPhoto) {
        setError('Please upload student photo');
        return;
      }

      await handleSave();
      navigate('/application-review');
    } catch (error) {
      setError('Failed to submit application');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="Stage 2 Application" />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white shadow-xl rounded-lg overflow-hidden">
          {/* Form Header */}
          <div className="bg-gradient-to-r from-teal-600 to-teal-700 px-6 py-8">
            <div className="flex items-center">
              <div className="bg-white bg-opacity-20 rounded-full p-3 mr-4">
                <GraduationCap className="h-8 w-8 text-white" />
              </div>
              <div>
                <h1 className="text-3xl font-bold text-white">Stage 2: Detailed Application</h1>
                <p className="text-teal-100 mt-2">Complete the comprehensive application form</p>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="px-6 py-8">
            <form className="space-y-8">
              {/* Student Photo Upload */}
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Student Photo Upload</h3>
                <div className="flex items-center space-x-4">
                  <div className="relative">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'studentPhoto')}
                      className="hidden"
                      id="studentPhoto"
                      name="studentPhoto"
                    />
                    <label
                      htmlFor="studentPhoto"
                      className="cursor-pointer bg-gray-100 hover:bg-gray-200 rounded-lg p-4 border-2 border-dashed border-gray-300"
                    >
                      <Upload className="h-8 w-8 text-gray-400 mx-auto mb-2" />
                      <p className="text-sm text-gray-600">Click to upload photo</p>
                    </label>
                  </div>
                  {formData.studentPhoto && (
                    renderFilePreview(formData.studentPhoto, 'studentPhoto')
                  )}
                </div>
              </div>

              {/* Permanent Address Details */}
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Permanent Address Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Country <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="country"
                      value={formData.country}
                      onChange={handleChange}
                      className="form-select"
                      required
                    >
                      {countries.map(country => (
                        <option key={country} value={country}>{country}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      State <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="state"
                      value={formData.state}
                      onChange={handleChange}
                      className="form-select"
                      required
                    >
                      {states.map(state => (
                        <option key={state} value={state}>{state}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      District <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="district"
                      value={formData.district}
                      onChange={handleChange}
                      className="form-select"
                      required
                    >
                      {districts.map(district => (
                        <option key={district} value={district}>{district}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      City <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="city"
                      value={formData.city}
                      onChange={handleChange}
                      className="form-select"
                      required
                    >
                      {cities.map(city => (
                        <option key={city} value={city}>{city}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address Line 1 <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="addressLine1"
                      value={formData.addressLine1}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter address line 1"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Address Line 2
                    </label>
                    <input
                      type="text"
                      name="addressLine2"
                      value={formData.addressLine2}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter address line 2"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Pin Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="pinCode"
                      value={formData.pinCode}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter pin code"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Is permanent address same as correspondence address?
                    </label>
                    <select
                      name="isPermanentSameAsCorrespondence"
                      value={formData.isPermanentSameAsCorrespondence}
                      onChange={handleChange}
                      className="form-select"
                    >
                      {yesNoOptions.map(option => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              {/* Current School Details */}
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Current School Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name of Current School <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="currentSchoolName"
                      value={formData.currentSchoolName}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter current school name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      From Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="studyingFromDate"
                      value={formData.studyingFromDate}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Till Date <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="date"
                      name="studyingTillDate"
                      value={formData.studyingTillDate}
                      onChange={handleChange}
                      className="form-input"
                      required
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reason for Leaving <span className="text-red-500">*</span>
                    </label>
                    <textarea
                      name="reasonForLeaving"
                      value={formData.reasonForLeaving}
                      onChange={handleChange}
                      className="form-input"
                      rows={3}
                      placeholder="Enter reason for leaving current school"
                      required
                    />
                  </div>
                </div>
              </div>

              {/* Parent 1 Details */}
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Parent 1 Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="parent1Name"
                      value={formData.parent1Name}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter parent name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Relationship <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="parent1Relationship"
                      value={formData.parent1Relationship}
                      onChange={handleChange}
                      className="form-select"
                      required
                    >
                      {relationships.map(rel => (
                        <option key={rel} value={rel}>{rel}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="email"
                      name="parent1Email"
                      value={formData.parent1Email}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter email address"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      name="parent1Phone"
                      value={formData.parent1Phone}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter phone number"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Profession/Occupation <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="parent1Profession"
                      value={formData.parent1Profession}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter profession"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Annual Gross Income
                    </label>
                    <select
                      name="parent1AnnualIncome"
                      value={formData.parent1AnnualIncome}
                      onChange={handleChange}
                      className="form-select"
                    >
                      <option value="">Select income range</option>
                      {incomeRanges.map(income => (
                        <option key={income} value={income}>{income}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Office Address
                    </label>
                    <textarea
                      name="parent1OfficeAddress"
                      value={formData.parent1OfficeAddress}
                      onChange={handleChange}
                      className="form-input"
                      rows={3}
                      placeholder="Enter office address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Photo Upload
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'parent1Photo')}
                      className="form-input"
                      name="parent1Photo"
                    />
                    {formData.parent1Photo && (
                      renderFilePreview(formData.parent1Photo, 'parent1Photo')
                    )}
                  </div>
                </div>
              </div>

              {/* Parent 2 Details */}
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Parent 2 Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Name <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="parent2Name"
                      value={formData.parent2Name}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter parent name"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Relationship <span className="text-red-500">*</span>
                    </label>
                    <select
                      name="parent2Relationship"
                      value={formData.parent2Relationship}
                      onChange={handleChange}
                      className="form-select"
                      required
                    >
                      {relationships.map(rel => (
                        <option key={rel} value={rel}>{rel}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email
                    </label>
                    <input
                      type="email"
                      name="parent2Email"
                      value={formData.parent2Email}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter email address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Phone
                    </label>
                    <input
                      type="tel"
                      name="parent2Phone"
                      value={formData.parent2Phone}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter phone number"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Profession/Occupation
                    </label>
                    <input
                      type="text"
                      name="parent2Profession"
                      value={formData.parent2Profession}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter profession"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Annual Gross Income
                    </label>
                    <select
                      name="parent2AnnualIncome"
                      value={formData.parent2AnnualIncome}
                      onChange={handleChange}
                      className="form-select"
                    >
                      <option value="">Select income range</option>
                      {incomeRanges.map(income => (
                        <option key={income} value={income}>{income}</option>
                      ))}
                    </select>
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Office Address
                    </label>
                    <textarea
                      name="parent2OfficeAddress"
                      value={formData.parent2OfficeAddress}
                      onChange={handleChange}
                      className="form-input"
                      rows={3}
                      placeholder="Enter office address"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Photo Upload
                    </label>
                    <input
                      type="file"
                      accept="image/*"
                      onChange={(e) => handleFileChange(e, 'parent2Photo')}
                      className="form-input"
                      name="parent2Photo"
                    />
                    {formData.parent2Photo && (
                      renderFilePreview(formData.parent2Photo, 'parent2Photo')
                    )}
                  </div>
                </div>
              </div>

              {/* Student Achievements */}
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Student Achievements</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Achievement Areas
                    </label>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      {achievementAreas.map(area => (
                        <label key={area} className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={formData.achievementsAreas.includes(area)}
                            onChange={() => handleAchievementChange(area)}
                            className="rounded border-gray-300 text-teal-600 focus:ring-teal-500"
                          />
                          <span className="text-sm text-gray-700">{area}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {formData.achievementsAreas.includes('Sports') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Sports Achievements
                      </label>
                      <textarea
                        name="sportsAchievements"
                        value={formData.sportsAchievements}
                        onChange={handleChange}
                        className="form-input"
                        rows={2}
                        placeholder="Specify sports achievements"
                      />
                    </div>
                  )}

                  {formData.achievementsAreas.includes('Performing arts') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Performing Arts Achievements
                      </label>
                      <textarea
                        name="performingArtsAchievements"
                        value={formData.performingArtsAchievements}
                        onChange={handleChange}
                        className="form-input"
                        rows={2}
                        placeholder="Specify performing arts achievements"
                      />
                    </div>
                  )}

                  {formData.achievementsAreas.includes('Positions of responsibility') && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Positions of Responsibility Achievements
                      </label>
                      <textarea
                        name="responsibilityAchievements"
                        value={formData.responsibilityAchievements}
                        onChange={handleChange}
                        className="form-input"
                        rows={2}
                        placeholder="Specify responsibility achievements"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Documents Upload */}
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Documents Upload</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Student Aadhar Card <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileChange(e, 'studentAadharCard')}
                      className="form-input"
                      name="studentAadharCard"
                      required
                    />
                    {formData.studentAadharCard && (
                      renderFilePreview(formData.studentAadharCard, 'studentAadharCard')
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Student Aadhar Number <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      name="studentAadharNumber"
                      value={formData.studentAadharNumber}
                      onChange={handleChange}
                      className="form-input"
                      placeholder="Enter Aadhar number"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Student Birth Certificate <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileChange(e, 'studentBirthCertificate')}
                      className="form-input"
                      name="studentBirthCertificate"
                      required
                    />
                    {formData.studentBirthCertificate && (
                      renderFilePreview(formData.studentBirthCertificate, 'studentBirthCertificate')
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Father's Aadhar Card
                    </label>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileChange(e, 'fatherAadharCard')}
                      className="form-input"
                      name="fatherAadharCard"
                    />
                    {formData.fatherAadharCard && (
                      renderFilePreview(formData.fatherAadharCard, 'fatherAadharCard')
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Father's PAN Card
                    </label>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileChange(e, 'fatherPanCard')}
                      className="form-input"
                      name="fatherPanCard"
                    />
                    {formData.fatherPanCard && (
                      renderFilePreview(formData.fatherPanCard, 'fatherPanCard')
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mother's Aadhar Card
                    </label>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileChange(e, 'motherAadharCard')}
                      className="form-input"
                      name="motherAadharCard"
                    />
                    {formData.motherAadharCard && (
                      renderFilePreview(formData.motherAadharCard, 'motherAadharCard')
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mother's PAN Card
                    </label>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileChange(e, 'motherPanCard')}
                      className="form-input"
                      name="motherPanCard"
                    />
                    {formData.motherPanCard && (
                      renderFilePreview(formData.motherPanCard, 'motherPanCard')
                    )}
                  </div>
                </div>
              </div>

              {/* Signatures */}
              <div className="border-b border-gray-200 pb-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Signatures</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Mother's Signature
                    </label>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileChange(e, 'motherSignature')}
                      className="form-input"
                      name="motherSignature"
                    />
                    {formData.motherSignature && (
                      renderFilePreview(formData.motherSignature, 'motherSignature')
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Father's Signature
                    </label>
                    <input
                      type="file"
                      accept="image/*,.pdf"
                      onChange={(e) => handleFileChange(e, 'fatherSignature')}
                      className="form-input"
                      name="fatherSignature"
                    />
                    {formData.fatherSignature && (
                      renderFilePreview(formData.fatherSignature, 'fatherSignature')
                    )}
                  </div>
                </div>
              </div>

              {/* Error and Success Messages */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4">
                  <p className="text-red-800 text-sm">{error}</p>
                </div>
              )}

              {success && (
                <div className="bg-green-50 border border-green-200 rounded-md p-4">
                  <p className="text-green-800 text-sm">{success}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  className="flex-1 flex justify-center items-center px-6 py-3 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {saving ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700 mr-2"></div>
                      Saving...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <Save className="h-4 w-4 mr-2" />
                      Save Draft
                    </div>
                  )}
                </button>

                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={loading}
                  className="flex-1 flex justify-center items-center px-6 py-3 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-teal-600 hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-teal-500 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      Submit & Continue
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </div>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Stage2Form;
